import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  X, ChevronRight, Check, MapPin, Navigation, Plus, Trash2,
  Thermometer, Users, Building2, ClipboardList, Package,
  Calendar, MessageSquare, CheckSquare, Send, AlertTriangle,
  Loader2, ChevronLeft, Search, Bot, FileText, Upload, LogOut
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Constants ──────────────────────────────────────────────────────────────

const WIZARD_STEPS = [
  { key: "locale_info",      label: "Dati Locale",   icon: Building2,    group: 1 },
  { key: "operatori",        label: "Operatori",      icon: Users,        group: 2 },
  { key: "frigoriferi",      label: "Frigoriferi",    icon: Thermometer,  group: 3 },
  { key: "aree_pulizia",     label: "Aree Pulizie",   icon: MapPin,       group: 4 },
  { key: "prodotti_pulizia", label: "Prodotti",       icon: Package,      group: 4 },
  { key: "task_pulizia",     label: "Task Pulizie",   icon: ClipboardList,group: 4 },
  { key: "bacheca_compiti",  label: "Bacheca",        icon: CheckSquare,  group: 5 },
  { key: "calendario",       label: "Calendario",     icon: Calendar,     group: 6 },
  { key: "segnalazioni",     label: "Segnalazioni",   icon: MessageSquare,group: 7 },
];

const TIMEZONES = [
  "Europe/Rome", "Europe/London", "Europe/Paris", "Europe/Berlin",
  "Europe/Madrid", "Europe/Lisbon", "America/New_York", "America/Los_Angeles",
];

const TIPOLOGIE_FRIGO = [
  "Armadio Refrigerato", "Cella Frigorifera", "Tavolo Refrigerato",
  "Frigo Vetrina", "Abbattitore", "Cantina Vino", "Congelatore",
];

const TEMP_DEFAULTS = {
  "Armadio Refrigerato": { min: 0, max: 4 },
  "Cella Frigorifera":   { min: 0, max: 4 },
  "Tavolo Refrigerato":  { min: 0, max: 4 },
  "Frigo Vetrina":       { min: 0, max: 8 },
  "Abbattitore":         { min: -40, max: 3 },
  "Cantina Vino":        { min: 10, max: 18 },
  "Congelatore":         { min: -25, max: -18 },
};

const AREE_PREDEFINITE = [
  { nome: "Cucina",      colore: "#84cc16" },
  { nome: "Sala",        colore: "#3b82f6" },
  { nome: "Bagni",       colore: "#ef4444" },
  { nome: "Spogliatoi",  colore: "#a855f7" },
  { nome: "Magazzino",   colore: "#f59e0b" },
];

const TIPI_PRODOTTO = [
  "Detergenti",
  "Disinfettanti e igienizzanti (HACCP)",
  "Sgrassatori",
  "Decalcificanti",
  "Prodotti per lavastoviglie",
  "Accessori per la pulizia",
];

const FREQUENZE = [
  { value: "dopo_uso",     label: "Dopo ogni utilizzo" },
  { value: "giornaliero",  label: "Giornaliero" },
  { value: "settimanale",  label: "Settimanale" },
  { value: "mensile",      label: "Mensile" },
];

const REPARTI = [
  { value: "tutti",        label: "Tutti" },
  { value: "cucina",       label: "Cucina" },
  { value: "sala",         label: "Sala" },
  { value: "laboratorio",  label: "Laboratorio" },
];

const SUGGERIMENTI_COMPITI = [
  "Buttare l'immondizia della plastica il giovedì",
  "Portare l'olio esausto in magazzino il venerdì",
  "Mettere in carica il POS a fine servizio",
  "Riunione staff il sabato alle 16",
];

const RUOLI_OPERATORE = [
  { value: "cucina", label: "Operatore Cucina" },
  { value: "sala", label: "Operatore Sala" },
  { value: "laboratorio", label: "Operatore Laboratorio" },
];

const TIPI_CONTRATTO = [
  { value: "non_definito", label: "Non definito" },
  { value: "full_time", label: "Full Time (40h)" },
  { value: "part_time_30", label: "Part Time 30h" },
  { value: "part_time_24", label: "Part Time 24h" },
  { value: "part_time_20", label: "Part Time 20h" },
  { value: "apprendistato", label: "Apprendistato" },
  { value: "stagionale", label: "Stagionale" },
  { value: "chiamata", label: "A Chiamata" },
];

// ─── Telegram Info Box ──────────────────────────────────────────────────────
// (rimosso dagli step — il suggerimento Telegram è nella schermata finale)


// Reusable save button used inside every form card
const SaveButton = ({ onClick, disabled, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    style={{
      background: "#f0fdf4",
      border: "2px dashed #22c55e",
      color: "#15803d",
      WebkitTextFillColor: "#15803d",
    }}
  >
    <Check className="h-4 w-4" style={{ color: "#16a34a", fill: "none" }} />
    <span style={{ color: "#15803d", WebkitTextFillColor: "#15803d" }}>{label}</span>
  </button>
);

const InfoBox = ({ icon: Icon, title, children, color = "#f59e0b" }) => (
  <div className="rounded-xl p-4 mt-4 border" style={{ borderColor: color + "40", background: color + "10" }}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-4 w-4" style={{ color }} />
      <p className="text-sm font-semibold" style={{ color }}>{title}</p>
    </div>
    <div className="text-xs text-slate-600 leading-relaxed">{children}</div>
  </div>
);

// ─── Progress Bar ────────────────────────────────────────────────────────────

const WizardProgress = ({ currentIndex, progress, steps }) => {
  // Mostra 9 cerchi numerati 1..N lineari, coerente con "Step X di N"
  return (
    <div className="flex items-center gap-1.5 px-6 py-4 border-b border-border bg-muted/30">
      {steps.map((s, i) => {
        const isDone = i < currentIndex || progress[s.key] === "completed" || progress[s.key] === "skipped";
        const isCurrent = i === currentIndex;
        return (
          <div key={s.key} className="flex items-center gap-1 flex-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all flex-shrink-0 ${
              isDone ? "bg-green-500 text-white" : isCurrent ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground border border-border"
            }`}>
              {isDone ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </div>
            {i < steps.length - 1 && (
              <div className={`h-0.5 flex-1 rounded-full transition-all ${isDone ? "bg-green-500" : isCurrent ? "bg-primary/40" : "bg-border"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Multi-Wizard Global Badge ──────────────────────────────────────────────
// Mostra "Wizard X di Y" con segmenti colorati. Solo se ci sono più wizard.
const WizardGlobalBadge = ({ index, total, completed, dark = false }) => {
  if (total <= 1) return null;
  return (
    <div className="flex items-center gap-2" data-testid="wizard-global-badge">
      {/* Segmenti colorati */}
      <div className="flex gap-0.5 items-center">
        {Array.from({ length: total }).map((_, i) => {
          const isCompleted = i < completed;
          const isCurrent   = i === index - 1;
          const bg = isCompleted ? "#10b981"
                   : isCurrent   ? "#6C63FF"
                   : dark ? "#334155" : "#e2e8f0";
          return (
            <div
              key={i}
              className="rounded-full transition-all"
              style={{
                width:  isCurrent ? 20 : 14,
                height: 6,
                background: bg,
              }}
            />
          );
        })}
      </div>
      <span
        className="text-[10px] font-semibold tabular-nums"
        style={{ color: dark ? "#94a3b8" : "#64748b" }}
      >
        {completed}/{total} wizard
      </span>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────

const OnboardingWizard = ({ onComplete, onPreviewModeChange }) => {
  const { getAuthHeaders, localeCorrente, locali, logout } = useAuth();
  const navigate = useNavigate();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState({});
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [hasTelegram, setHasTelegram] = useState(false);
  const [telegramAddonOwned, setTelegramAddonOwned] = useState(false); // true se l'utente ha acquistato l'add-on Bot Telegram
  const [saving, setSaving] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [previewTransition, setPreviewTransition] = useState(false);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [completionCounts, setCompletionCounts] = useState(null);
  const [wizardMeta, setWizardMeta] = useState({ wizard_index: 1, total_wizards: 1, completed_wizards: 0 });

  // Step 1 - Locale
  const [localeForm, setLocaleForm] = useState({
    nome: localeCorrente?.nome || "",
    telefono: localeCorrente?.telefono || "",
    timezone: localeCorrente?.timezone || "Europe/Rome",
    lat: null,
    lon: null,
  });
  // Address search (Nominatim autocomplete)
  const [addressSearch, setAddressSearch] = useState(localeCorrente?.indirizzo || "");
  const [addressResults, setAddressResults] = useState([]);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(localeCorrente?.indirizzo ? { display_name: localeCorrente.indirizzo, lat: null, lon: null } : null);
  const addressDebounceRef = useRef(null);
  const skipSearchRef = useRef(false);
  // Persist nav-back target across StrictMode double-invocation of effects
  const wizNavStepRef = useRef(typeof sessionStorage !== "undefined" ? sessionStorage.getItem("wizard_nav_step") : null);

  // Step 2 - Operatore
  const [operatoreForm, setOperatoreForm] = useState({ nome: "", password: "", ruolo: "cucina", email: "", telefono: "", tipo_contratto: "non_definito", ore_settimanali: "", data_assunzione: "", data_fine_contratto: "", note_contratto: "", giorni_ferie_annuali: "" });
  const [operatori, setOperatori] = useState([]);

  // Step 3 - Frigorifero
  const [frigoForm, setFrigoForm] = useState({ nome: "", tipologia: "", temperatura_min: 0, temperatura_max: 4, note: "" });
  const [frigoriferi, setFrigoriferi] = useState([]);
  const [showFrigoForm, setShowFrigoForm] = useState(true);

  // Step 4a - Aree
  const [areeSelezionate, setAreeSelezionate] = useState(
    AREE_PREDEFINITE.map(a => ({ ...a, selected: true }))
  );
  const [nuovaArea, setNuovaArea] = useState("");
  const [nuovaAreaColore, setNuovaAreaColore] = useState("#6b7280");
  const [areeCreate, setAreeCreate] = useState([]);
  const [areeStep4Done, setAreeStep4Done] = useState(false);

  // Step 4b - Prodotti
  const [prodottoForm, setProdottoForm] = useState({ nome: "", tipo: "", note: "", scheda_tecnica_data: null, scheda_tecnica_nome: "" });
  const [prodotti, setProdotti] = useState([]);
  const [showProdottoForm, setShowProdottoForm] = useState(true);

  // Step 4c - Task Pulizie
  const [taskPuliziaForm, setTaskPuliziaForm] = useState({
    nome: "", descrizione: "", area_id: "", frequenza: "giornaliero",
    giorni_settimana: [], giorno_mese: null,
    assegnazione_tipo: "reparto", reparto: "cucina", operatori_ids: [], prodotti_ids: []
  });
  const [tasksPulizia, setTasksPulizia] = useState([]);
  const [showTaskPuliziaForm, setShowTaskPuliziaForm] = useState(true);

  // Step 5 - Bacheca Compiti
  const [compitoForm, setCompitoForm] = useState({ 
    nome: "", descrizione: "", frequenza: "giornaliero", 
    assegnazione_tipo: "reparto", reparto: "tutti", 
    orario_inizio: "", orario_fine: "",
    tipo_scadenza: "ricorrente", data_scadenza: "",
    data_inizio_finestra: "", data_fine_finestra: "",
    giorni_anticipo_alert: 7
  });
  const [compiti, setCompiti] = useState([]);
  const [showCompitoForm, setShowCompitoForm] = useState(true);

  const headers = getAuthHeaders();
  const localeId = localeCorrente?.id;

  // ─── Init ────────────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const [wizRes, tlgRes, subRes] = await Promise.allSettled([
          axios.get(`${API}/wizard`, { headers }),
          axios.get(`${API}/telegram/config`, { headers }),
          axios.get(`${API}/billing/subscription`, { headers }),
        ]);
        if (cancelled) return;  // StrictMode: ignore first invocation result
        if (wizRes.status === "fulfilled") {
          const d = wizRes.value.data || {};
          const p = d.progress?.steps || {};
          setProgress(p);
          // Aggiorna meta per progress bar globale
          if (d.total_wizards) {
            setWizardMeta({
              wizard_index:      d.wizard_index      ?? 1,
              total_wizards:     d.total_wizards      ?? 1,
              completed_wizards: d.completed_wizards  ?? 0,
            });
          }
          // Check if user navigated back (via goPrev) – resume at saved step
          // wizNavStepRef survives StrictMode double-invocation of this effect
          const savedStep = wizNavStepRef.current;
          if (savedStep) {
            wizNavStepRef.current = null;
            sessionStorage.removeItem("wizard_nav_step");
            const savedIdx = WIZARD_STEPS.findIndex(s => s.key === savedStep);
            if (savedIdx !== -1) {
              setCurrentIndex(savedIdx);
              return;
            }
          }
          // Find the first pending step
          const idx = WIZARD_STEPS.findIndex(s => !p[s.key] || p[s.key] === "pending");
          if (idx !== -1) setCurrentIndex(idx);
        }
        if (tlgRes.status === "fulfilled") {
          setHasTelegram(!!tlgRes.value.data?.bot_token_configured);
        }
        if (subRes.status === "fulfilled") {
          const sub = subRes.value.data?.subscription || {};
          setTelegramAddonOwned(
            !!sub.telegram_attivo || (sub.addon_telegram_qty || 0) > 0
          );
        }
      } catch {}
      finally {
        // Sblocca l'overlay "Caricamento wizard" anche in caso di errore di rete
        if (!cancelled) setProgressLoaded(true);
      }
    };
    init();
    return () => { cancelled = true; };
  }, []);

  // Sync locale form when localeCorrente changes
  useEffect(() => {
    if (localeCorrente) {
      setLocaleForm(prev => ({
        ...prev,
        nome: localeCorrente.nome || prev.nome,
        telefono: localeCorrente.telefono || prev.telefono,
        timezone: localeCorrente.timezone || "Europe/Rome",
      }));
      if (localeCorrente.indirizzo && !selectedAddress) {
        skipSearchRef.current = true;
        setAddressSearch(localeCorrente.indirizzo);
        setSelectedAddress({ display_name: localeCorrente.indirizzo, lat: null, lon: null });
      }
    }
  }, [localeCorrente?.id]);

  // Nominatim debounced search
  useEffect(() => {
    if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
    const query = addressSearch.trim();
    if (query.length < 3) { setAddressResults([]); return; }
    if (skipSearchRef.current) { skipSearchRef.current = false; return; }
    addressDebounceRef.current = setTimeout(async () => {
      setSearchingAddress(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`,
          { headers: { "Accept-Language": "it" } }
        );
        setAddressResults(await res.json());
      } catch {
        toast.error("Errore nella ricerca dell'indirizzo");
      } finally {
        setSearchingAddress(false);
      }
    }, 500);
    return () => clearTimeout(addressDebounceRef.current);
  }, [addressSearch]);

  const selectAddress = (result) => {
    setSelectedAddress(result);
    setLocaleForm(prev => ({ ...prev, lat: parseFloat(result.lat), lon: parseFloat(result.lon) }));
    setAddressResults([]);
    skipSearchRef.current = true;
    setAddressSearch(result.display_name);
    toast.success("Indirizzo e coordinate impostati");
  };

  // ─── Step completion ────────────────────────────────────────────────────

  const markStep = useCallback(async (stepKey, status = "completed") => {
    await axios.put(`${API}/wizard/step/${stepKey}`, { status }, { headers });
    setProgress(prev => ({ ...prev, [stepKey]: status }));
  }, [headers]);

  const goNext = () => {
    if (currentIndex < WIZARD_STEPS.length - 1) {
      setCurrentIndex(i => i + 1);
    }
  };

  const goPrev = () => {
    if (currentIndex > 0) {
      const prevStep = WIZARD_STEPS[currentIndex - 1];
      // Only persist to sessionStorage when navigating back to a preview step
      // (preview steps use different route/Layout configs, causing remounts)
      if (prevStep && (prevStep.key === "calendario" || prevStep.key === "segnalazioni")) {
        sessionStorage.setItem("wizard_nav_step", prevStep.key);
      }
      setCurrentIndex(i => i - 1);
    }
  };

  // Scroll in cima al contenuto ad ogni cambio step
  const wizardContentRef = useRef(null);
  useEffect(() => {
    wizardContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentIndex]);

  // ─── GPS ─────────────────────────────────────────────────────────────────

  const rilevaPosizione = () => {
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setLocaleForm(prev => ({ ...prev, lat: latitude, lon: longitude }));
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`, { headers: { "Accept-Language": "it" } });
          const data = await res.json();
          if (data.display_name) {
            skipSearchRef.current = true;
            setAddressSearch(data.display_name);
            setSelectedAddress({ display_name: data.display_name, lat: latitude, lon: longitude });
            setAddressResults([]);
          }
        } catch {}
        setGpsLoading(false);
        toast.success("Posizione GPS rilevata");
      },
      () => { setGpsLoading(false); toast.error("Impossibile rilevare la posizione"); }
    );
  };

  // ─── Step 1 handler ──────────────────────────────────────────────────────

  const saveLocaleInfo = async () => {
    if (!localeForm.nome.trim()) return toast.error("Il nome del locale è obbligatorio");
    const indirizzoSalvato = selectedAddress?.display_name || addressSearch.trim();
    if (!indirizzoSalvato) return toast.error("Seleziona un indirizzo dalla lista per continuare");
    setSaving(true);
    try {
      const payload = {
        nome: localeForm.nome,
        indirizzo: indirizzoSalvato,
        timezone: localeForm.timezone,
        telefono: localeForm.telefono || undefined,
      };
      if (localeForm.lat != null && localeForm.lon != null) {
        payload.coordinate_locale = { lat: localeForm.lat, lng: localeForm.lon };
      }
      await axios.put(`${API}/locali/${localeId}`, payload, { headers });
      await markStep("locale_info");
      goNext();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // ─── Step 2 handler ──────────────────────────────────────────────────────

  const fetchOperatori = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/utenti`, { headers });
      setOperatori(res.data.filter(u => u.ruolo !== "admin"));
    } catch {}
  }, []);

  const saveOperatore = async () => {
    if (!operatoreForm.nome.trim()) return toast.error("Il nome è obbligatorio");
    if (!operatoreForm.email?.trim() && !operatoreForm.telefono?.trim()) return toast.error("Email o telefono obbligatorio per identificare l'operatore");
    if (!operatoreForm.password.trim() || operatoreForm.password.length < 4) return toast.error("Il PIN deve essere di almeno 4 caratteri");
    if (!operatoreForm.telefono.trim()) return toast.error("Il telefono è obbligatorio");
    setSaving(true);
    try {
      await axios.post(`${API}/utenti`, {
        nome: operatoreForm.nome,
        password: operatoreForm.password,
        ruolo: operatoreForm.ruolo,
        email: operatoreForm.email || undefined,
        telefono: operatoreForm.telefono || undefined,
        tipo_contratto: operatoreForm.tipo_contratto,
        ore_settimanali: operatoreForm.ore_settimanali ? parseInt(operatoreForm.ore_settimanali) : undefined,
        data_assunzione: operatoreForm.data_assunzione || undefined,
        data_fine_contratto: operatoreForm.data_fine_contratto || undefined,
        note_contratto: operatoreForm.note_contratto || undefined,
        giorni_ferie_annuali: operatoreForm.giorni_ferie_annuali ? parseInt(operatoreForm.giorni_ferie_annuali) : undefined,
        locali_ids: localeId ? [localeId] : [],
        funzionalita: [],
      }, { headers });
      toast.success("Operatore aggiunto");
      setOperatoreForm({ nome: "", password: "", ruolo: "cucina", email: "", telefono: "", tipo_contratto: "non_definito", ore_settimanali: "", data_assunzione: "", data_fine_contratto: "", note_contratto: "", giorni_ferie_annuali: "" });
      await fetchOperatori();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella creazione operatore");
    } finally {
      setSaving(false);
    }
  };

  const continueOperatori = async () => {
    const hasCucina = operatori.some(o => o.ruolo === "cucina");
    if (!hasCucina) return toast.error("Aggiungi almeno un operatore cucina per continuare");
    await markStep("operatori");
    goNext();
  };

  // ─── Step 3 handler ──────────────────────────────────────────────────────

  const saveFrigo = async () => {
    if (!frigoForm.nome.trim()) return toast.error("Il nome è obbligatorio");
    if (!frigoForm.tipologia) return toast.error("Seleziona la tipologia");
    setSaving(true);
    try {
      const res = await axios.post(`${API}/frigoriferi`, {
        locale_id: localeId,
        nome: frigoForm.nome,
        tipologia: frigoForm.tipologia,
        temperatura_min: parseFloat(frigoForm.temperatura_min),
        temperatura_max: parseFloat(frigoForm.temperatura_max),
        note: frigoForm.note || undefined,
      }, { headers });
      setFrigoriferi(prev => [...prev, res.data]);
      setFrigoForm({ nome: "", tipologia: "", temperatura_min: 0, temperatura_max: 4, note: "" });
      setShowFrigoForm(false);
      toast.success("Frigorifero aggiunto");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const continueFrigoriferi = async () => {
    if (frigoriferi.length === 0) return toast.error("Aggiungi almeno un frigorifero per continuare");
    await markStep("frigoriferi");
    goNext();
  };

  // ─── Step 4a handler ─────────────────────────────────────────────────────

  const toggleArea = (idx) => {
    setAreeSelezionate(prev => prev.map((a, i) => i === idx ? { ...a, selected: !a.selected } : a));
  };

  const addCustomArea = () => {
    if (!nuovaArea.trim()) return;
    setAreeSelezionate(prev => [...prev, { nome: nuovaArea, colore: nuovaAreaColore, selected: true }]);
    setNuovaArea("");
    setNuovaAreaColore("#6b7280");
  };

  const saveAree = async () => {
    const toCreate = areeSelezionate.filter(a => a.selected);
    if (toCreate.length === 0) return toast.error("Seleziona almeno un'area");
    setSaving(true);
    try {
      // Fetch existing areas to avoid duplicates
      const existingRes = await axios.get(`${API}/aree-pulizia?locale_id=${localeId}`, { headers });
      const existing = existingRes.data || [];
      const existingByNome = new Map(existing.map(a => [a.nome, a]));

      const result = [];
      for (const area of toCreate) {
        if (existingByNome.has(area.nome)) {
          // Already exists — reuse it, don't create a duplicate
          result.push(existingByNome.get(area.nome));
        } else {
          // Create only if truly new
          const res = await axios.post(`${API}/aree-pulizia`, {
            locale_id: localeId,
            nome: area.nome,
            colore: area.colore,
          }, { headers });
          result.push(res.data);
        }
      }
      // Deduplicate result by nome
      const seen = new Set();
      const unique = result.filter(a => {
        if (seen.has(a.nome)) return false;
        seen.add(a.nome);
        return true;
      });
      setAreeCreate(unique);
      setAreeStep4Done(true);
      await markStep("aree_pulizia");
      goNext();
      toast.success(`${unique.length} aree configurate`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio aree");
    } finally {
      setSaving(false);
    }
  };

  // Fetch existing aree when on step 4a (in case already created)
  useEffect(() => {
    const stepKey = WIZARD_STEPS[currentIndex]?.key;
    // Fetch aree when on aree_pulizia OR task_pulizia step (needed for area dropdown)
    if ((stepKey === "aree_pulizia" || stepKey === "task_pulizia") && localeId) {
      axios.get(`${API}/aree-pulizia?locale_id=${localeId}`, { headers })
        .then(res => {
          if (res.data.length > 0) {
            // Deduplicate by nome (keep first occurrence)
            const seen = new Set();
            const unique = res.data.filter(a => {
              if (seen.has(a.nome)) return false;
              seen.add(a.nome);
              return true;
            });
            setAreeCreate(unique);
            // On step 4a: pre-select existing areas in checkboxes
            if (stepKey === "aree_pulizia") {
              setAreeSelezionate(prev => prev.map(a => ({
                ...a,
                selected: unique.some(u => u.nome === a.nome) ? true : a.selected,
              })));
            }
          }
        })
        .catch(() => {});
    }
    if (stepKey === "operatori" && operatori.length === 0) {
      fetchOperatori();
    }
    // Fetch existing frigoriferi when on frigoriferi step
    if (stepKey === "frigoriferi" && localeId && frigoriferi.length === 0) {
      axios.get(`${API}/frigoriferi?locale_id=${localeId}`, { headers })
        .then(res => { if (res.data.length > 0) { setFrigoriferi(res.data); setShowFrigoForm(false); } })
        .catch(() => {});
    }
    // Fetch existing prodotti when on prodotti_pulizia step
    if (stepKey === "prodotti_pulizia" && localeId && prodotti.length === 0) {
      axios.get(`${API}/prodotti-pulizia?locale_id=${localeId}`, { headers })
        .then(res => { if (res.data.length > 0) { setProdotti(res.data); setShowProdottoForm(false); } })
        .catch(() => {});
    }
    // Fetch existing task pulizie when on task_pulizia step
    if (stepKey === "task_pulizia" && localeId && tasksPulizia.length === 0) {
      axios.get(`${API}/task-pulizia?locale_id=${localeId}`, { headers })
        .then(res => { if (res.data.length > 0) { setTasksPulizia(res.data); setShowTaskPuliziaForm(false); } })
        .catch(() => {});
    }
    // Also fetch operatori for task_pulizia assignment
    if (stepKey === "task_pulizia" && operatori.length === 0) {
      fetchOperatori();
    }
    // Fetch prodotti when on task_pulizia step (needed for prodotti_ids picker)
    if (stepKey === "task_pulizia" && localeId && prodotti.length === 0) {
      axios.get(`${API}/prodotti-pulizia?locale_id=${localeId}`, { headers })
        .then(res => { if (res.data.length > 0) setProdotti(res.data); })
        .catch(() => {});
    }
  }, [currentIndex, localeId]);

  // ─── Step 4b handler ─────────────────────────────────────────────────────

  const saveProdotto = async () => {
    if (!prodottoForm.nome.trim()) return toast.error("Il nome è obbligatorio");
    if (!prodottoForm.tipo) return toast.error("Seleziona la tipologia");
    setSaving(true);
    try {
      const res = await axios.post(`${API}/prodotti-pulizia`, {
        locale_id: localeId,
        nome: prodottoForm.nome,
        tipo: prodottoForm.tipo,
        note: prodottoForm.note || undefined,
        scheda_tecnica_data: prodottoForm.scheda_tecnica_data || undefined,
        scheda_tecnica_nome: prodottoForm.scheda_tecnica_nome || undefined,
      }, { headers });
      setProdotti(prev => [...prev, res.data]);
      setProdottoForm({ nome: "", tipo: "", note: "", scheda_tecnica_data: null, scheda_tecnica_nome: "" });
      setShowProdottoForm(false);
      toast.success("Prodotto aggiunto");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const continueProdotti = async () => {
    if (prodotti.length < 2) return toast.error("Inserisci almeno 2 prodotti per continuare");
    await markStep("prodotti_pulizia");
    goNext();
  };

  // ─── Step 4c handler ─────────────────────────────────────────────────────

  const saveTaskPulizia = async () => {
    if (!taskPuliziaForm.nome.trim()) return toast.error("Il nome del task è obbligatorio");
    if (!taskPuliziaForm.area_id) return toast.error("Seleziona l'area");
    if (taskPuliziaForm.assegnazione_tipo === "reparto" && !taskPuliziaForm.reparto) return toast.error("Seleziona il reparto");
    if (taskPuliziaForm.assegnazione_tipo === "operatori" && taskPuliziaForm.operatori_ids.length === 0) return toast.error("Seleziona almeno un operatore");
    setSaving(true);
    try {
      const res = await axios.post(`${API}/task-pulizia`, {
        locale_id: localeId,
        nome: taskPuliziaForm.nome,
        descrizione: taskPuliziaForm.descrizione || undefined,
        area_id: taskPuliziaForm.area_id,
        frequenza: taskPuliziaForm.frequenza,
        assegnazione_tipo: taskPuliziaForm.assegnazione_tipo,
        reparto: taskPuliziaForm.assegnazione_tipo === "reparto" ? taskPuliziaForm.reparto : null,
        operatori_ids: taskPuliziaForm.assegnazione_tipo === "operatori" ? taskPuliziaForm.operatori_ids : [],
        prodotti_ids: taskPuliziaForm.prodotti_ids,
        giorni_settimana: taskPuliziaForm.giorni_settimana,
        giorno_mese: taskPuliziaForm.giorno_mese || undefined,
      }, { headers });
      setTasksPulizia(prev => [...prev, res.data]);
      setTaskPuliziaForm(prev => ({ ...prev, nome: "", descrizione: "", area_id: "", prodotti_ids: [], giorni_settimana: [], giorno_mese: null }));
      setShowTaskPuliziaForm(false);
      toast.success("Task di pulizia aggiunto");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const continueTaskPulizie = async () => {
    const areeConTask = new Set(tasksPulizia.map(t => t.area_id));
    const areeSenzaTask = areeCreate.filter(a => !areeConTask.has(a.id));
    if (areeSenzaTask.length > 0) {
      return toast.error(`Mancano task per: ${areeSenzaTask.map(a => a.nome).join(", ")}`);
    }
    await markStep("task_pulizia");
    goNext();
  };

  // ─── Step 5 handler ──────────────────────────────────────────────────────

  const saveCompito = async (nome = null) => {
    const nomeTask = nome || compitoForm.nome;
    if (!nomeTask.trim()) return toast.error("Il nome del compito è obbligatorio");
    setSaving(true);
    try {
      const res = await axios.post(`${API}/task-gestione`, {
        locale_id: localeId,
        nome: nomeTask,
        descrizione: compitoForm.descrizione || undefined,
        frequenza: compitoForm.frequenza,
        tipo: "operativa",
        assegnazione_tipo: compitoForm.assegnazione_tipo,
        reparto: compitoForm.assegnazione_tipo === "reparto" ? compitoForm.reparto : null,
        orario_inizio: compitoForm.orario_inizio || undefined,
        orario_fine: compitoForm.orario_fine || undefined,
        tipo_scadenza: compitoForm.tipo_scadenza,
        data_scadenza: compitoForm.data_scadenza || undefined,
        data_inizio_finestra: compitoForm.data_inizio_finestra || undefined,
        data_fine_finestra: compitoForm.data_fine_finestra || undefined,
        giorni_anticipo_alert: compitoForm.giorni_anticipo_alert,
      }, { headers });
      setCompiti(prev => [...prev, res.data]);
      setCompitoForm({ 
        nome: "", descrizione: "", frequenza: "giornaliero", 
        assegnazione_tipo: "reparto", reparto: "tutti", 
        orario_inizio: "", orario_fine: "",
        tipo_scadenza: "ricorrente", data_scadenza: "",
        data_inizio_finestra: "", data_fine_finestra: "",
        giorni_anticipo_alert: 7
      });
      setShowCompitoForm(false);
      toast.success("Compito aggiunto");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const continueCompiti = async () => {
    if (compiti.length === 0) return toast.error("Aggiungi almeno un compito per continuare");
    await markStep("bacheca_compiti");
    goNext();
  };

  // ─── Step 6/7 handlers ───────────────────────────────────────────────────

  const completeCalendario = async () => {
    await markStep("calendario");
    goNext();
  };

  const completeSegnalazioni = async () => {
    await markStep("segnalazioni");
    setShowCompletionScreen(true); // mostra subito la schermata

    // Fetch conteggi reali dal DB via endpoint dedicato (non richiede locale_id)
    try {
      const headers = getAuthHeaders();
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${baseUrl}/api/wizard/summary`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCompletionCounts(data);
      }
    } catch {
      // fallback silenzioso ai dati locali
      setCompletionCounts({
        operatori:   operatori.length,
        frigoriferi: frigoriferi.length,
        aree:        areeCreate.length,
        task:        tasksPulizia.length + compiti.length,
      });
    }
  };

  // ─── Step Renderer ────────────────────────────────────────────────────────

  const currentStep = WIZARD_STEPS[currentIndex];

  // ─── Preview mode: navigate to real page for calendario/segnalazioni ────
  const PREVIEW_ROUTES = { calendario: "/calendario?view=settimana", segnalazioni: "/operativita/segnalazioni" };
  const isPreviewMode = Object.keys(PREVIEW_ROUTES).includes(currentStep.key);
  const location = useLocation();

  // overlay-curtain: attivo finché l'URL non corrisponde alla route attesa
  // (così il flash del wizard centrato pre-navigate viene sempre coperto)
  const expectedRoute = PREVIEW_ROUTES[currentStep.key] || "";
  const expectedPathname = expectedRoute.split("?")[0];
  const urlMatches = expectedPathname ? location.pathname === expectedPathname : true;
  const showCurtain = isPreviewMode && (!urlMatches || previewTransition);

  useEffect(() => {
    const route = PREVIEW_ROUTES[currentStep.key];
    if (route) {
      setPreviewTransition(true);
      navigate(route);
      const t = setTimeout(() => setPreviewTransition(false), 500);
      return () => clearTimeout(t);
    }
  }, [currentStep.key]); // eslint-disable-line

  useEffect(() => {
    if (onPreviewModeChange) onPreviewModeChange(isPreviewMode);
    return () => { if (!isPreviewMode && onPreviewModeChange) onPreviewModeChange(false); };
  }, [isPreviewMode, onPreviewModeChange]); // eslint-disable-line

  const renderStep = () => {
    switch (currentStep.key) {

      // ── STEP 1: Dati Locale ──────────────────────────────────────────────
      case "locale_info":
        return (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-600 mb-4">
                Completa i dati del tuo locale: nome, fuso orario e indirizzo. Sono la base per il corretto funzionamento di tutte le attività dell'app.
              </p>
            </div>
            <InfoBox icon={Building2} title="Perché è importante?" color="#f59e0b">
              Il fuso orario e l'indirizzo del tuo locale sono la base di ogni registrazione HACCP. Senza di essi, le temperature registrate non hanno un riferimento temporale preciso — il che può rendere i tuoi report non conformi durante un'ispezione sanitaria. Bastano 2 minuti ora per avere tutto in regola.
            </InfoBox>

            <div className="space-y-3 mt-4">
              <div>
            <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Nome Locale *</Label>
                <Input
                  data-testid="wizard-locale-nome"
                  className="mt-1"
                  value={localeForm.nome}
                  onChange={e => setLocaleForm(p => ({ ...p, nome: e.target.value }))}
                  placeholder="es. Ristorante La Bella Italia"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Telefono</Label>
                <Input
                  data-testid="wizard-locale-telefono"
                  className="mt-1"
                  value={localeForm.telefono}
                  onChange={e => setLocaleForm(p => ({ ...p, telefono: e.target.value }))}
                  placeholder="es. 06 12345678"
                  type="tel"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Fuso Orario *</Label>
                <Select value={localeForm.timezone} onValueChange={v => setLocaleForm(p => ({ ...p, timezone: v }))}>
                  <SelectTrigger className="mt-1" data-testid="wizard-locale-timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Indirizzo *</Label>
                <div className="relative mt-1">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                    {searchingAddress
                      ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      : <Search className="h-4 w-4 text-muted-foreground" />}
                  </div>
                  <Input
                    data-testid="wizard-locale-indirizzo"
                    className="pl-9 pr-10"
                    value={addressSearch}
                    onChange={e => {
                      setAddressSearch(e.target.value);
                      setSelectedAddress(null);
                    }}
                    onKeyDown={e => e.key === "Enter" && e.preventDefault()}
                    placeholder="Inizia a scrivere l'indirizzo..."
                  />
                  <button
                    type="button"
                    onClick={rilevaPosizione}
                    disabled={gpsLoading}
                    data-testid="wizard-gps-btn"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
                    title="Usa GPS"
                  >
                    {gpsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
                  </button>
                </div>
                {addressSearch.length > 0 && addressSearch.length < 3 && (
                  <p className="text-xs text-slate-500 mt-1">Scrivi almeno 3 caratteri per cercare</p>
                )}

                {/* Risultati ricerca */}
                {addressResults.length > 0 && (
                  <div className="mt-1 border border-border rounded-xl overflow-hidden max-h-44 overflow-y-auto bg-white shadow-lg z-50 relative">
                    {addressResults.map((r, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => selectAddress(r)}
                        data-testid={`wizard-address-result-${i}`}
                        className="w-full text-left px-3 py-2.5 text-sm border-b border-gray-100 last:border-0 hover:bg-blue-50 transition-colors flex items-start gap-2"
                        style={{ color: "#1a1a1a" }}
                      >
                        <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                        <span className="leading-tight">{r.display_name}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Feedback indirizzo selezionato */}
                {selectedAddress && !addressResults.length && (
                  <div className="mt-2 rounded-xl p-2.5 border-2 flex items-start gap-2" style={{ borderColor: "#3b82f6", background: "#eff6ff" }}>
                    <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: "#2563eb" }} />
                    <div>
                      <p className="text-xs font-bold text-blue-700 uppercase tracking-wide">
                        {localeForm.lat ? "Da GPS" : "Da ricerca indirizzo"}
                      </p>
                      <p className="text-xs text-blue-900 mt-0.5">{selectedAddress.display_name}</p>
                      {localeForm.lat && (
                        <p className="text-xs text-blue-600 mt-0.5">
                          Lat: {localeForm.lat.toFixed(6)}, Lon: {localeForm.lon.toFixed(6)}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>


            <div className="flex gap-2 pt-4">
              <Button
                data-testid="wizard-step1-continua"
                className="flex-1"
                onClick={saveLocaleInfo}
                disabled={saving}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Continua <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        );

      // ── STEP 2: Operatori ────────────────────────────────────────────────
      case "operatori":
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              In questa area devi inserire gli operatori che lavorano al tuo locale e che dovranno utilizzare l'app Yollo per assicurare che tutti i compiti vengano eseguiti.
            </p>
            <InfoBox icon={Users} title="Perché è importante?" color="#3b82f6">
              Il protocollo HACCP richiede che ogni controllo igienico e ogni registrazione di temperatura sia tracciata e firmata da un operatore identificabile. L'operatore cucina è la figura chiave: è il responsabile diretto delle aree di preparazione degli alimenti.
            </InfoBox>

            {operatori.length > 0 && (
              <div className="space-y-2">
                {operatori.map(op => (
                  <div key={op.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 border border-border">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${op.ruolo === "cucina" ? "bg-green-500" : "bg-blue-500"}`} />
                    <span className="text-sm font-medium">{op.nome || op.email || op.telefono}</span>
                    <span className="text-xs text-muted-foreground ml-auto capitalize">{op.ruolo}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="rounded-xl border border-border p-4 bg-card space-y-3">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Nuovo operatore</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nome Completo *</Label>
                  <Input
                    data-testid="wizard-op-nome"
                    className="mt-1"
                    value={operatoreForm.nome}
                    onChange={e => setOperatoreForm(p => ({ ...p, nome: e.target.value }))}
                    placeholder="Mario Rossi"
                  />
                </div>
                <div>
                  <Label className="text-xs">PIN/Password *</Label>
                  <Input
                    data-testid="wizard-op-pin"
                    type="text"
                    className="mt-1"
                    value={operatoreForm.password}
                    onChange={e => setOperatoreForm(p => ({ ...p, password: e.target.value }))}
                    placeholder="1234"
                    maxLength={8}
                  />
                </div>
                <div>
                  <Label className="text-xs">Ruolo *</Label>
                  <Select value={operatoreForm.ruolo} onValueChange={v => setOperatoreForm(p => ({ ...p, ruolo: v }))}>
                    <SelectTrigger className="mt-1" data-testid="wizard-op-ruolo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RUOLI_OPERATORE.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Telefono *</Label>
                  <Input
                    data-testid="wizard-op-telefono"
                    type="tel"
                    className="mt-1"
                    value={operatoreForm.telefono}
                    onChange={e => setOperatoreForm(p => ({ ...p, telefono: e.target.value }))}
                    placeholder="333 1234567"
                  />
                </div>
              </div>

              <div className="border-t border-border pt-3 mt-1">
                <p className="text-xs font-semibold text-slate-600 mb-2">Dati facoltativi</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-700">Email <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                    <Input
                      data-testid="wizard-op-email"
                      type="email"
                      className="mt-1"
                      value={operatoreForm.email}
                      onChange={e => setOperatoreForm(p => ({ ...p, email: e.target.value }))}
                      placeholder="mario@example.com"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-700">Tipo Contratto <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                    <Select value={operatoreForm.tipo_contratto} onValueChange={v => setOperatoreForm(p => ({ ...p, tipo_contratto: v }))}>
                      <SelectTrigger className="mt-1" data-testid="wizard-op-contratto">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPI_CONTRATTO.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-700">Ore Settimanali <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                    <Input
                      type="number"
                      className="mt-1"
                      value={operatoreForm.ore_settimanali}
                      onChange={e => setOperatoreForm(p => ({ ...p, ore_settimanali: e.target.value }))}
                      placeholder="40"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-700">Data Assunzione <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                    <Input
                      type="date"
                      className="mt-1"
                      value={operatoreForm.data_assunzione}
                      onChange={e => setOperatoreForm(p => ({ ...p, data_assunzione: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-700">Fine Contratto <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                    <Input
                      type="date"
                      className="mt-1"
                      value={operatoreForm.data_fine_contratto}
                      onChange={e => setOperatoreForm(p => ({ ...p, data_fine_contratto: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-xs text-slate-700">Note Contratto <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                  <Input
                    className="mt-1"
                    value={operatoreForm.note_contratto}
                    onChange={e => setOperatoreForm(p => ({ ...p, note_contratto: e.target.value }))}
                    placeholder="Note sul contratto"
                  />
                </div>
              </div>
              <SaveButton
                onClick={saveOperatore}
                disabled={saving}
                label={saving ? "Salvataggio..." : "Salva operatore"}
              />
            </div>

            {operatori.length > 0 && !operatori.some(o => o.ruolo === "cucina") && (
              <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                Aggiungi almeno un operatore cucina per continuare
              </p>
            )}


            <div className="flex gap-2 pt-2">
              {operatori.some(o => o.ruolo === "cucina") && (
                <Button
                  data-testid="wizard-op-salta"
                  variant="outline"
                  className="flex-1"
                  onClick={continueOperatori}
                >
                  Salta per ora
                </Button>
              )}
              <Button
                data-testid="wizard-op-continua"
                className="flex-1"
                onClick={continueOperatori}
                disabled={saving}
              >
                Continua <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        );

      // ── STEP 3: Frigoriferi ──────────────────────────────────────────────
      case "frigoriferi":
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Registra tutti i frigoriferi del locale. Tenere traccia costante delle temperature è un obbligo HACCP e ti protegge in caso di ispezione sanitaria.
            </p>
            <InfoBox icon={Thermometer} title="Perché è importante?" color="#ef4444">
              Il controllo delle temperature è il cuore del protocollo HACCP. Ogni frigorifero che non viene monitorato è un potenziale rischio sanitario — e in caso di ispezione, la mancanza di registrazioni è equiparata a una non conformità.
            </InfoBox>

            {frigoriferi.length > 0 && (
              <div className="space-y-2">
                {frigoriferi.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 border border-border">
                    <Thermometer className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    <span className="text-sm font-medium">{f.nome}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{f.tipologia}</span>
                    <span className="text-xs text-muted-foreground">{f.temperatura_min}° / {f.temperatura_max}°</span>
                  </div>
                ))}
                {!showFrigoForm && (
                  <p className="text-sm font-medium text-green-600">Ottimo! Hai altri frigoriferi da registrare?</p>
                )}
              </div>
            )}

            {(showFrigoForm || frigoriferi.length === 0) && (
              <div className="rounded-xl border border-border p-4 bg-card space-y-3">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Nuovo frigorifero</p>
                <div>
                  <Label className="text-xs">Nome *</Label>
                  <Input
                    data-testid="wizard-frigo-nome"
                    className="mt-1"
                    value={frigoForm.nome}
                    onChange={e => setFrigoForm(p => ({ ...p, nome: e.target.value }))}
                    placeholder="es. Frigo principale cucina"
                  />
                </div>
                <div>
                  <Label className="text-xs">Tipologia *</Label>
                  <Select
                    value={frigoForm.tipologia}
                    onValueChange={v => {
                      const defaults = TEMP_DEFAULTS[v] || { min: 0, max: 4 };
                      setFrigoForm(p => ({ ...p, tipologia: v, temperatura_min: defaults.min, temperatura_max: defaults.max }));
                    }}
                  >
                    <SelectTrigger className="mt-1" data-testid="wizard-frigo-tipologia">
                      <SelectValue placeholder="Seleziona..." />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOLOGIE_FRIGO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Temp. Min (°C) *</Label>
                    <Input
                      data-testid="wizard-frigo-tmin"
                      type="number"
                      className="mt-1"
                      value={frigoForm.temperatura_min}
                      onChange={e => setFrigoForm(p => ({ ...p, temperatura_min: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Temp. Max (°C) *</Label>
                    <Input
                      data-testid="wizard-frigo-tmax"
                      type="number"
                      className="mt-1"
                      value={frigoForm.temperatura_max}
                      onChange={e => setFrigoForm(p => ({ ...p, temperatura_max: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Note</Label>
                  <Input
                    data-testid="wizard-frigo-note"
                    className="mt-1"
                    value={frigoForm.note}
                    onChange={e => setFrigoForm(p => ({ ...p, note: e.target.value }))}
                    placeholder="es. Posizionato vicino alla cella"
                  />
                </div>
                <SaveButton
                  onClick={saveFrigo}
                  disabled={saving}
                  label={saving ? "Salvataggio..." : "Salva frigorifero"}
                />
              </div>
            )}

            {!showFrigoForm && frigoriferi.length > 0 && (
              <button
                type="button"
                onClick={() => setShowFrigoForm(true)}
                className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-muted-foreground hover:text-foreground border border-dashed border-border rounded-xl hover:bg-muted/50 transition-all"
              >
                <Plus className="h-3.5 w-3.5" /> Hai un altro frigorifero? Aggiungilo
              </button>
            )}


            <div className="flex gap-2 pt-2">
              <Button
                data-testid="wizard-frigo-continua"
                className="flex-1"
                onClick={continueFrigoriferi}
                disabled={frigoriferi.length === 0}
              >
                {frigoriferi.length > 0 ? "No, continua →" : "Continua"} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        );

      // ── STEP 4a: Aree Pulizia ────────────────────────────────────────────
      case "aree_pulizia":
        return (
          <div className="space-y-4">
            <div className="rounded-xl p-3 border border-amber-300 bg-amber-50 dark:bg-amber-950/20 flex gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Per configurare al meglio questa sezione, è indispensabile avere a portata di mano il <strong>fascicolo HACCP</strong> del tuo locale, fornito dal tuo consulente.
              </p>
            </div>

            <p className="text-sm text-slate-600">
              In questa sezione devi inserire le aree presenti nel tuo locale: cucina, sala, bagni, ecc. — inseriscile tutte per organizzare al meglio le attività di pulizia.
            </p>

            <InfoBox icon={Building2} title="Perché è importante?" color="#3b82f6">
              Le aree di pulizia sono la struttura portante del tuo piano HACCP. Ogni attività di pulizia deve essere associata a un'area specifica. Consulta il tuo fascicolo HACCP per non tralasciare nessuna zona.
            </InfoBox>

            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Seleziona le aree del tuo locale</p>
              {areeSelezionate.map((area, idx) => (
                <label
                  key={idx}
                  data-testid={`wizard-area-${area.nome.toLowerCase().replace(/\s/g, "-")}`}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${area.selected ? "border-primary/40 bg-primary/5" : "border-border bg-card hover:bg-muted/50"}`}
                >
                  <input
                    type="checkbox"
                    checked={area.selected}
                    onChange={() => toggleArea(idx)}
                    className="h-4 w-4 accent-primary"
                  />
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: area.colore }} />
                  <span className="text-sm font-medium">{area.nome}</span>
                </label>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="color"
                className="h-10 w-10 p-1 rounded-md border border-border cursor-pointer flex-shrink-0"
                value={nuovaAreaColore}
                onChange={e => setNuovaAreaColore(e.target.value)}
                title="Scegli colore area"
              />
              <Input
                data-testid="wizard-area-custom"
                value={nuovaArea}
                onChange={e => setNuovaArea(e.target.value)}
                placeholder="Aggiungi area personalizzata..."
                onKeyDown={e => e.key === "Enter" && addCustomArea()}
              />
              <Button variant="outline" size="sm" onClick={addCustomArea} className="flex-shrink-0">
                <Plus className="h-4 w-4" />
              </Button>
            </div>


            <div className="flex gap-2 pt-2">
              <Button
                data-testid="wizard-aree-continua"
                className="flex-1"
                onClick={saveAree}
                disabled={saving || !areeSelezionate.some(a => a.selected)}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Continua <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        );

      // ── STEP 4b: Prodotti Pulizia ────────────────────────────────────────
      case "prodotti_pulizia":
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              In questa sezione devi registrare i prodotti che utilizzi per le pulizie, obbligatorio per l'HACCP perché in caso di ispezione vengono richiesti.
            </p>
            <InfoBox icon={Package} title="Perché è importante?" color="#8b5cf6">
              Il piano HACCP richiede che ogni attività di pulizia sia eseguita con prodotti specifici e certificati. In caso di ispezione, ti verrà chiesto non solo cosa viene pulito, ma anche con cosa.
            </InfoBox>

            {prodotti.length > 0 && (
              <div className="space-y-2">
                {prodotti.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 border border-border">
                    <Package className="h-4 w-4 text-purple-500 flex-shrink-0" />
                    <span className="text-sm font-medium">{p.nome}</span>
                    {p.scheda_tecnica_url && (
                      <FileText className="h-3.5 w-3.5 text-green-500 flex-shrink-0" title="Scheda tecnica caricata" />
                    )}
                    <span className="text-xs text-muted-foreground ml-auto">{p.tipo}</span>
                  </div>
                ))}
                {prodotti.length >= 2 && !showProdottoForm && (
                  <p className="text-sm font-medium text-green-600">
                    Ottimo! Hai inserito {prodotti.length} prodotti. Vuoi aggiungerne altri prima di continuare?
                  </p>
                )}
              </div>
            )}

            {(showProdottoForm || prodotti.length === 0) && (
              <div className="rounded-xl border border-border p-4 bg-card space-y-3">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Nuovo prodotto</p>
                <div>
                  <Label className="text-xs">Nome Prodotto *</Label>
                  <Input
                    data-testid="wizard-prodotto-nome"
                    className="mt-1"
                    value={prodottoForm.nome}
                    onChange={e => setProdottoForm(p => ({ ...p, nome: e.target.value }))}
                    placeholder="es. Candeggina professionale"
                  />
                </div>
                <div>
                  <Label className="text-xs">Tipologia *</Label>
                  <Select value={prodottoForm.tipo} onValueChange={v => setProdottoForm(p => ({ ...p, tipo: v }))}>
                    <SelectTrigger className="mt-1" data-testid="wizard-prodotto-tipo">
                      <SelectValue placeholder="Seleziona categoria..." />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPI_PRODOTTO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-700">Note <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                  <Input
                    data-testid="wizard-prodotto-note"
                    className="mt-1"
                    value={prodottoForm.note}
                    onChange={e => setProdottoForm(p => ({ ...p, note: e.target.value }))}
                    placeholder="es. Solo per superfici alimentari"
                  />
                </div>

                {/* Scheda Tecnica PDF */}
                <div>
                  <Label className="text-xs text-slate-700">Scheda Tecnica PDF <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                  <div className="mt-1">
                    {prodottoForm.scheda_tecnica_nome ? (
                      <div className="flex items-center gap-2 p-2.5 rounded-lg border border-green-200 bg-green-50">
                        <FileText className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-xs text-green-800 flex-1 truncate">{prodottoForm.scheda_tecnica_nome}</span>
                        <button
                          type="button"
                          onClick={() => setProdottoForm(p => ({ ...p, scheda_tecnica_data: null, scheda_tecnica_nome: "" }))}
                          className="text-xs text-red-500 hover:text-red-700 flex-shrink-0 font-medium"
                        >
                          Rimuovi
                        </button>
                      </div>
                    ) : (
                      <label
                        data-testid="wizard-prodotto-pdf"
                        className="flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-border hover:border-primary/50 hover:bg-muted/30 cursor-pointer transition-all"
                      >
                        <Upload className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-slate-600">Carica PDF scheda tecnica</span>
                        <input
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          onChange={e => {
                            const file = e.target.files[0];
                            if (!file) return;
                            if (file.size > 5 * 1024 * 1024) {
                              toast.error("Il file non può superare 5 MB");
                              return;
                            }
                            const reader = new FileReader();
                            reader.onload = ev => {
                              const base64 = ev.target.result.split(",")[1];
                              setProdottoForm(p => ({ ...p, scheda_tecnica_data: base64, scheda_tecnica_nome: file.name }));
                            };
                            reader.readAsDataURL(file);
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>
                <SaveButton
                  onClick={saveProdotto}
                  disabled={saving}
                  label={saving ? "Salvataggio..." : "Salva prodotto"}
                />
              </div>
            )}

            {!showProdottoForm && prodotti.length > 0 && (
              <button
                type="button"
                onClick={() => setShowProdottoForm(true)}
                className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-muted-foreground hover:text-foreground border border-dashed border-border rounded-xl hover:bg-muted/50 transition-all"
              >
                <Plus className="h-3.5 w-3.5" /> Aggiungi un altro prodotto
              </button>
            )}

            {prodotti.length < 2 && prodotti.length > 0 && (
              <p className="text-xs text-amber-600 font-medium">Inserisci almeno {2 - prodotti.length} altro{2 - prodotti.length > 1 ? "i" : ""} prodotto{2 - prodotti.length > 1 ? "i" : ""} per continuare</p>
            )}


            <div className="flex gap-2 pt-2">
              {prodotti.length >= 2 && !showProdottoForm && (
                <Button variant="outline" className="flex-1" onClick={continueProdotti} data-testid="wizard-prodotti-salta">
                  No, continua →
                </Button>
              )}
              <Button
                data-testid="wizard-prodotti-continua"
                className="flex-1"
                onClick={continueProdotti}
                disabled={prodotti.length < 2}
              >
                Continua <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        );

      // ── STEP 4c: Task Pulizie ────────────────────────────────────────────
      case "task_pulizia":
        const areeConTask = new Set(tasksPulizia.map(t => t.area_id));
        const areeMancanti = areeCreate.filter(a => !areeConTask.has(a.id));
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Crea le routine di pulizia per ogni area seguendo il tuo manuale HACCP. Ogni area deve avere almeno una task.
            </p>
            <InfoBox icon={ClipboardList} title="Perché è importante?" color="#10b981">
              Le task di pulizia sono il cuore del tuo piano HACCP quotidiano. Ogni area del locale deve avere almeno una routine di pulizia definita — è un requisito fondamentale del protocollo.
            </InfoBox>

            {tasksPulizia.length > 0 && (
              <div className="space-y-2">
                {tasksPulizia.map((t, i) => {
                  const area = areeCreate.find(a => a.id === t.area_id);
                  return (
                    <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 border border-border">
                      <ClipboardList className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm font-medium">{t.nome}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{area?.nome || "-"}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {areeMancanti.length > 0 && tasksPulizia.length > 0 && (
              <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                Mancano task per: {areeMancanti.map(a => a.nome).join(", ")}
              </p>
            )}

            {(showTaskPuliziaForm || tasksPulizia.length === 0) && (
              <div className="rounded-xl border border-border p-4 bg-card space-y-3">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Nuovo task di pulizia</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Nome Task *</Label>
                    <Input
                      data-testid="wizard-task-nome"
                      className="mt-1"
                      value={taskPuliziaForm.nome}
                      onChange={e => setTaskPuliziaForm(p => ({ ...p, nome: e.target.value }))}
                      placeholder="es. Sanificazione piano lavoro"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Area *</Label>
                    <Select value={taskPuliziaForm.area_id} onValueChange={v => setTaskPuliziaForm(p => ({ ...p, area_id: v }))}>
                      <SelectTrigger className="mt-1" data-testid="wizard-task-area">
                        <SelectValue placeholder="Seleziona..." />
                      </SelectTrigger>
                      <SelectContent>
                        {areeCreate.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Assegna a *</Label>
                  <div className="flex gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => setTaskPuliziaForm(p => ({ ...p, assegnazione_tipo: "reparto" }))}
                      className={`flex-1 py-2 px-3 rounded-lg border text-xs font-semibold transition-all ${
                        taskPuliziaForm.assegnazione_tipo === "reparto"
                          ? "border-indigo-400 bg-indigo-50 text-indigo-700"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      Reparto
                    </button>
                    <button
                      type="button"
                      onClick={() => setTaskPuliziaForm(p => ({ ...p, assegnazione_tipo: "operatori" }))}
                      className={`flex-1 py-2 px-3 rounded-lg border text-xs font-semibold transition-all ${
                        taskPuliziaForm.assegnazione_tipo === "operatori"
                          ? "border-indigo-400 bg-indigo-50 text-indigo-700"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      Operatori
                    </button>
                  </div>

                  {taskPuliziaForm.assegnazione_tipo === "reparto" && (
                    <Select value={taskPuliziaForm.reparto} onValueChange={v => setTaskPuliziaForm(p => ({ ...p, reparto: v }))} >
                      <SelectTrigger className="mt-2" data-testid="wizard-task-reparto">
                        <SelectValue placeholder="Seleziona reparto..." />
                      </SelectTrigger>
                      <SelectContent>
                        {REPARTI.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}

                  {taskPuliziaForm.assegnazione_tipo === "operatori" && (
                    <div className="mt-2 space-y-1">
                      {operatori.length === 0 ? (
                        <p className="text-xs text-slate-500">Nessun operatore disponibile. Usa "Reparto".</p>
                      ) : (
                        operatori.map(op => (
                          <label key={op.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted cursor-pointer">
                            <input
                              type="checkbox"
                              checked={taskPuliziaForm.operatori_ids.includes(op.id)}
                              onChange={e => {
                                const ids = e.target.checked
                                  ? [...taskPuliziaForm.operatori_ids, op.id]
                                  : taskPuliziaForm.operatori_ids.filter(id => id !== op.id);
                                setTaskPuliziaForm(p => ({ ...p, operatori_ids: ids }));
                              }}
                              className="h-3.5 w-3.5 accent-primary"
                            />
                            <span className="text-xs">{op.nome || op.email || op.telefono}</span>
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <div className="border-t border-border pt-3 mt-1">
                  <p className="text-xs font-semibold text-slate-600 mb-2">Dati facoltativi</p>
                  <div>
                    <Label className="text-xs text-slate-700">Descrizione <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                    <Input
                      data-testid="wizard-task-descrizione"
                      className="mt-1"
                      value={taskPuliziaForm.descrizione}
                      onChange={e => setTaskPuliziaForm(p => ({ ...p, descrizione: e.target.value }))}
                      placeholder="es. Usare guanti e igienizzante"
                    />
                  </div>
                  <div className="mt-3">
                    <Label className="text-xs text-slate-700">Frequenza <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                    <Select value={taskPuliziaForm.frequenza} onValueChange={v => setTaskPuliziaForm(p => ({ ...p, frequenza: v }))}>
                      <SelectTrigger className="mt-1" data-testid="wizard-task-frequenza">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENZE.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {taskPuliziaForm.frequenza === "settimanale" && (
                    <div className="mt-3">
                      <Label className="text-xs">Giorni della settimana</Label>
                      <div className="flex gap-1 mt-1">
                        {[{l:"Lun", v:1}, {l:"Mar", v:2}, {l:"Mer", v:3}, {l:"Gio", v:4}, {l:"Ven", v:5}, {l:"Sab", v:6}, {l:"Dom", v:0}].map(g => (
                          <button
                            key={g.v}
                            type="button"
                            onClick={() => {
                              const newDays = taskPuliziaForm.giorni_settimana.includes(g.v)
                                ? taskPuliziaForm.giorni_settimana.filter(d => d !== g.v)
                                : [...taskPuliziaForm.giorni_settimana, g.v];
                              setTaskPuliziaForm(p => ({ ...p, giorni_settimana: newDays }));
                            }}
                            className={`flex-1 py-1.5 text-xs rounded-md border transition-colors font-semibold ${
                              taskPuliziaForm.giorni_settimana.includes(g.v)
                                ? "bg-indigo-50 border-indigo-400 text-indigo-700"
                                : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                            }`}
                          >
                            {g.l}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {taskPuliziaForm.frequenza === "mensile" && (
                    <div className="mt-3">
                      <Label className="text-xs">Giorno del mese</Label>
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        className="mt-1"
                        value={taskPuliziaForm.giorno_mese || ""}
                        onChange={e => setTaskPuliziaForm(p => ({ ...p, giorno_mese: parseInt(e.target.value) || null }))}
                        placeholder="es. 15"
                      />
                    </div>
                  )}

                  <div className="mt-3">
                    <Label className="text-xs text-slate-700">Prodotti utilizzati <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                    <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
                      {prodotti.length === 0 ? (
                        <p className="text-xs text-slate-400 px-3 py-2.5 italic">
                          Nessun prodotto disponibile — aggiungili nello step precedente.
                        </p>
                      ) : (
                        <div className="space-y-0 max-h-28 overflow-y-auto p-2">
                          {prodotti.map(prod => (
                            <label key={prod.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-100 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={taskPuliziaForm.prodotti_ids.includes(prod.id)}
                                onChange={e => {
                                  const ids = e.target.checked
                                    ? [...taskPuliziaForm.prodotti_ids, prod.id]
                                    : taskPuliziaForm.prodotti_ids.filter(id => id !== prod.id);
                                  setTaskPuliziaForm(p => ({ ...p, prodotti_ids: ids }));
                                }}
                                className="h-3.5 w-3.5 accent-primary"
                              />
                              <span className="text-xs text-slate-700">{prod.nome}</span>
                              <span className="text-xs text-slate-400 ml-auto">{prod.tipo}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <SaveButton
                  onClick={saveTaskPulizia}
                  disabled={saving}
                  label={saving ? "Salvataggio..." : "Salva task"}
                />
              </div>
            )}

            {!showTaskPuliziaForm && tasksPulizia.length > 0 && (
              <button
                type="button"
                onClick={() => setShowTaskPuliziaForm(true)}
                className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-muted-foreground hover:text-foreground border border-dashed border-border rounded-xl hover:bg-muted/50 transition-all"
              >
                <Plus className="h-3.5 w-3.5" /> Aggiungi un altro task di pulizia
              </button>
            )}


            <div className="flex gap-2 pt-2">
              <Button
                data-testid="wizard-task-continua"
                className="flex-1"
                onClick={continueTaskPulizie}
                disabled={tasksPulizia.length === 0}
              >
                {areeMancanti.length > 0
                  ? `Mancano task per: ${areeMancanti.map(a => a.nome).slice(0, 2).join(", ")}${areeMancanti.length > 2 ? "..." : ""}`
                  : "Continua →"
                }
                {areeMancanti.length === 0 && <ChevronRight className="h-4 w-4 ml-1" />}
              </Button>
            </div>
          </div>
        );

      // ── STEP 5: Bacheca Compiti ──────────────────────────────────────────
      case "bacheca_compiti":
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Oltre agli obblighi HACCP, qui inserisci tutte le routine organizzative del locale: dalla raccolta differenziata, al caricamento delle batterie del POS, alla riunione settimanale.
            </p>
            <InfoBox icon={CheckSquare} title="Perché è importante?" color="#6366f1">
              La bacheca compiti è il tuo strumento per trasformare le buone abitudini in routine sistematiche. Inserire le attività ricorrenti ti permette di delegarle, tracciarle e non doverci più pensare.
            </InfoBox>

            <div>
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Idee rapide — clicca per precompilare il form</p>
              <div className="flex flex-wrap gap-2">
                {SUGGERIMENTI_COMPITI.map(s => (
                  <button
                    key={s}
                    onClick={() => {
                      setCompitoForm(p => ({ ...p, nome: s }));
                      setShowCompitoForm(true);
                    }}
                    className="text-xs px-3 py-1.5 rounded-full border border-slate-300 bg-slate-50 text-slate-700 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 transition-all"
                  >
                    + {s}
                  </button>
                ))}
              </div>
            </div>

            {compiti.length > 0 && (
              <div className="space-y-2">
                {compiti.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 border border-border">
                    <Check className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                    <span className="text-sm font-medium">{c.nome}</span>
                    <span className="text-xs text-muted-foreground ml-auto capitalize">{c.frequenza}</span>
                  </div>
                ))}
                {!showCompitoForm && (
                  <p className="text-sm font-medium text-green-600">
                    Ottimo! Vuoi aggiungere altre routine prima di continuare?
                  </p>
                )}
              </div>
            )}

            {(showCompitoForm || compiti.length === 0) && (
              <div className="rounded-xl border border-border p-4 bg-card space-y-3">
                <div>
                  <Label className="text-xs">Nome Compito *</Label>
                  <Input
                    data-testid="wizard-compito-nome"
                    className="mt-1"
                    value={compitoForm.nome}
                    onChange={e => setCompitoForm(p => ({ ...p, nome: e.target.value }))}
                    placeholder="es. Buttare immondizia ogni giovedì"
                  />
                </div>

                <div className="border-t border-border pt-3 mt-1">
                  <p className="text-xs font-semibold text-slate-600 mb-2">Dati facoltativi</p>
                  <div>
                    <Label className="text-xs text-slate-700">Descrizione <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                    <Input
                      data-testid="wizard-compito-descrizione"
                      className="mt-1"
                      value={compitoForm.descrizione}
                      onChange={e => setCompitoForm(p => ({ ...p, descrizione: e.target.value }))}
                      placeholder="es. Usare i sacchi grandi blu"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <Label className="text-xs text-slate-700">Frequenza <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                      <Select value={compitoForm.frequenza} onValueChange={v => setCompitoForm(p => ({ ...p, frequenza: v }))}>
                        <SelectTrigger className="mt-1" data-testid="wizard-compito-frequenza">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FREQUENZE.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700">Reparto <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                      <Select value={compitoForm.reparto} onValueChange={v => setCompitoForm(p => ({ ...p, reparto: v }))}>
                        <SelectTrigger className="mt-1" data-testid="wizard-compito-reparto">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {REPARTI.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700">Orario inizio <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                      <Input
                        data-testid="wizard-compito-orario-inizio"
                        type="time"
                        className="mt-1"
                        value={compitoForm.orario_inizio}
                        onChange={e => setCompitoForm(p => ({ ...p, orario_inizio: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700">Orario fine <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                      <Input
                        data-testid="wizard-compito-orario-fine"
                        type="time"
                        className="mt-1"
                        value={compitoForm.orario_fine}
                        onChange={e => setCompitoForm(p => ({ ...p, orario_fine: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <Label className="text-xs text-slate-700">Tipo Scadenza <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                      <Select value={compitoForm.tipo_scadenza} onValueChange={v => setCompitoForm(p => ({ ...p, tipo_scadenza: v }))}>
                        <SelectTrigger className="mt-1" data-testid="wizard-compito-tipo-scadenza">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ricorrente">Ricorrente</SelectItem>
                          <SelectItem value="una_volta">Una volta sola</SelectItem>
                          <SelectItem value="finestra">Finestra temporale</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700">Giorni anticipo alert <span className="text-[10px] italic font-normal text-slate-400">(facoltativo)</span></Label>
                      <Input
                        type="number"
                        className="mt-1"
                        value={compitoForm.giorni_anticipo_alert}
                        onChange={e => setCompitoForm(p => ({ ...p, giorni_anticipo_alert: parseInt(e.target.value) || 0 }))}
                        placeholder="7"
                      />
                    </div>
                  </div>

                  {compitoForm.tipo_scadenza === "una_volta" && (
                    <div className="mt-3">
                      <Label className="text-xs">Data Scadenza</Label>
                      <Input
                        type="date"
                        className="mt-1"
                        value={compitoForm.data_scadenza}
                        onChange={e => setCompitoForm(p => ({ ...p, data_scadenza: e.target.value }))}
                      />
                    </div>
                  )}

                  {compitoForm.tipo_scadenza === "finestra" && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <Label className="text-xs">Data Inizio Finestra</Label>
                        <Input
                          type="date"
                          className="mt-1"
                          value={compitoForm.data_inizio_finestra}
                          onChange={e => setCompitoForm(p => ({ ...p, data_inizio_finestra: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Data Fine Finestra</Label>
                        <Input
                          type="date"
                          className="mt-1"
                          value={compitoForm.data_fine_finestra}
                          onChange={e => setCompitoForm(p => ({ ...p, data_fine_finestra: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <SaveButton
                  onClick={() => saveCompito()}
                  disabled={saving}
                  label={saving ? "Salvataggio..." : "Salva compito"}
                />
              </div>
            )}

            {!showCompitoForm && compiti.length > 0 && (
              <button
                type="button"
                onClick={() => setShowCompitoForm(true)}
                className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-muted-foreground hover:text-foreground border border-dashed border-border rounded-xl hover:bg-muted/50 transition-all"
              >
                <Plus className="h-3.5 w-3.5" /> Aggiungi un altro compito
              </button>
            )}


            <div className="flex gap-2 pt-2">
              {compiti.length > 0 && !showCompitoForm && (
                <Button variant="outline" className="flex-1" onClick={continueCompiti} data-testid="wizard-compiti-salta">
                  No, continua →
                </Button>
              )}
              <Button
                data-testid="wizard-compiti-continua"
                className="flex-1"
                onClick={continueCompiti}
                disabled={compiti.length === 0}
              >
                Continua <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        );

      // ── STEP 6: Calendario ───────────────────────────────────────────────
      case "calendario":
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              Da questa vista puoi monitorare in tempo reale <strong>tutte le attività che i tuoi operatori svolgono</strong> ogni giorno: pulizie, controlli temperature, task organizzative.
            </p>
            <div className="rounded-xl border border-border p-4 bg-muted/30 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span>Verde = completato</span>
                <div className="w-3 h-3 rounded-full bg-red-500 ml-4" />
                <span>Rosso = in ritardo</span>
              </div>
              <p className="text-xs text-slate-600">
                Ogni mattina, un'occhiata al calendario ti basta per sapere come sta andando il tuo locale.
              </p>
            </div>

            <div className="flex gap-1 rounded-xl border border-border overflow-hidden">
              {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((g, i) => (
                <div key={g} className={`flex-1 p-2 text-center border-r border-border last:border-0 ${i < 5 ? "bg-card" : "bg-muted/50"}`}>
                  <p className="text-xs font-medium text-muted-foreground">{g}</p>
                  <div className="mt-2 space-y-1">
                    <div className={`h-1.5 rounded-full ${i < 4 ? "bg-green-400" : i === 4 ? "bg-amber-400" : "bg-muted"}`} />
                    <div className={`h-1.5 rounded-full ${i < 3 ? "bg-green-400" : "bg-muted"}`} />
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs text-slate-500">
              Accedi al calendario completo dalla sidebar: <strong>HACCP &amp; Operatività → Calendario</strong>
            </p>


            <Button
              data-testid="wizard-calendario-avanti"
              className="w-full mt-2"
              onClick={completeCalendario}
            >
              Capito, avanti <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        );

      // ── STEP 7: Segnalazioni ─────────────────────────────────────────────
      case "segnalazioni":
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              In questa area puoi vedere le varie richieste e segnalazioni dei tuoi operatori. Rispondi direttamente dall'app o apri tu stesso una segnalazione verso lo staff.
            </p>

            <div className="space-y-3">
              {[
                { color: "#ef4444", label: "Problematiche", desc: "Guasti, carenze, situazioni da risolvere" },
                { color: "#f59e0b", label: "Proposte", desc: "Idee per migliorare il locale" },
                { color: "#3b82f6", label: "Informative", desc: "Comunicazioni generali allo staff" },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: item.color }} />
                  <div>
                    <p className="text-sm font-medium">{item.label}</p>
                    <p className="text-xs text-slate-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>


            <Button
              data-testid="wizard-concludi"
              className="w-full mt-2"
              onClick={completeSegnalazioni}
            >
              <Check className="h-4 w-4 mr-2" />
              Ho capito, concludi →
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  const stepTitles = {
    locale_info:      { title: "Completa i dati del tuo locale", subtitle: "Ci vogliono meno di 2 minuti" },
    operatori:        { title: "Aggiungi i tuoi operatori", subtitle: "Almeno un operatore cucina è necessario per iniziare" },
    frigoriferi:      { title: "Registra i tuoi frigoriferi", subtitle: "Almeno un frigorifero è necessario per il controllo temperature" },
    aree_pulizia:     { title: "Piano Pulizie — Aree", subtitle: "Definisci le aree del tuo locale" },
    prodotti_pulizia: { title: "Piano Pulizie — Prodotti", subtitle: "Inserisci i prodotti di pulizia certificati" },
    task_pulizia:     { title: "Piano Pulizie — Task", subtitle: "Crea le routine di pulizia per ogni area" },
    bacheca_compiti:  { title: "Organizza le routine del tuo locale", subtitle: "Non solo obblighi di legge" },
    calendario:       { title: "Il tuo calendario operativo", subtitle: "Tutto in un colpo d'occhio" },
    segnalazioni:     { title: "Le segnalazioni del tuo staff", subtitle: "Problemi, idee e comunicazioni — tutto in un posto" },
  };

  const { title, subtitle } = stepTitles[currentStep.key] || {};

  // ─── Loading guard: finché il progress non è caricato dal server, mostra solo overlay ───
  // Evita il "flash dello step 1" quando il wizard si rimonta mentre cambia pagina.
  if (!progressLoaded) {
    return (
      <div
        className="fixed inset-0 z-[200] pointer-events-none"
        style={{
          background: "linear-gradient(135deg, rgba(2,6,23,0.85) 0%, rgba(15,23,42,0.78) 100%)",
          backdropFilter: "blur(8px)",
        }}
        aria-hidden="true"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 rounded-full border-2 border-indigo-300/30 border-t-indigo-300 animate-spin" />
            <p className="text-xs uppercase tracking-widest font-bold text-indigo-300">Caricamento wizard</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Completion screen ────────────────────────────────────────────────────
  if (showCompletionScreen) {
    const completionStats = [
      { icon: Users,         value: completionCounts?.operatori   ?? operatori.length,                  label: "Operatori",       color: "#6C63FF", bg: "#f0effe" },
      { icon: Thermometer,   value: completionCounts?.frigoriferi ?? frigoriferi.length,                label: "Frigoriferi",     color: "#3b82f6", bg: "#eff6ff" },
      { icon: MapPin,        value: completionCounts?.aree        ?? areeCreate.length,                 label: "Aree pulizia",    color: "#059669", bg: "#ecfdf5" },
      { icon: ClipboardList, value: completionCounts?.task        ?? (tasksPulizia.length + compiti.length), label: "Task configurati", color: "#7c3aed", bg: "#f5f3ff" },
    ];
    return (
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center px-6 overflow-y-auto"
        style={{ background: "#f7f5ff" }}
        data-testid="wizard-completion-screen"
      >
        <style>{`
          @keyframes wiz-pop {
            0%   { transform: scale(0.4); opacity: 0; }
            65%  { transform: scale(1.08); }
            100% { transform: scale(1);   opacity: 1; }
          }
          @keyframes wiz-slide-up {
            from { transform: translateY(24px); opacity: 0; }
            to   { transform: translateY(0);    opacity: 1; }
          }
          @keyframes wiz-glow-violet {
            0%,100% { box-shadow: 0 0 0 0 rgba(108,99,255,0.15), 0 8px 32px rgba(108,99,255,0.25); }
            50%     { box-shadow: 0 0 0 8px rgba(108,99,255,0.08), 0 8px 40px rgba(108,99,255,0.35); }
          }
          @keyframes wiz-spin-in {
            from { transform: rotate(-120deg) scale(0); opacity: 0; }
            to   { transform: rotate(0deg)   scale(1); opacity: 1; }
          }
        `}</style>

        <div className="w-full max-w-sm text-center py-8">
          {/* Icona successo */}
          <div
            className="w-24 h-24 rounded-full mx-auto mb-7 flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #6C63FF 0%, #4f46e5 100%)",
              animation: "wiz-pop 0.65s cubic-bezier(0.175,0.885,0.32,1.275) both, wiz-glow-violet 3s ease-in-out 0.65s infinite",
            }}
          >
            <Check
              className="h-12 w-12 text-white"
              strokeWidth={3}
              style={{ animation: "wiz-spin-in 0.5s cubic-bezier(0.175,0.885,0.32,1.275) 0.2s both" }}
            />
          </div>

          {/* Titolo */}
          <h2
            className="text-3xl font-black mb-2"
            style={{ color: "#1e1b4b", animation: "wiz-slide-up 0.45s ease-out 0.15s both" }}
          >
            Ottimo lavoro!
          </h2>
          <p
            className="text-sm mb-8 leading-relaxed"
            style={{ color: "#6b7280", animation: "wiz-slide-up 0.45s ease-out 0.25s both" }}
          >
            Il tuo locale è configurato e pronto.<br />
            Yollo inizia a lavorare per te da adesso.
          </p>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            {completionStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl p-4 text-left"
                style={{
                  background: stat.bg,
                  border: `1.5px solid ${stat.color}25`,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  animation: `wiz-pop 0.5s cubic-bezier(0.175,0.885,0.32,1.275) ${["0ms","80ms","160ms","240ms"][completionStats.indexOf(stat)]} both`,
                }}
                data-testid={`wizard-stat-${stat.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <stat.icon className="h-5 w-5 mb-2" style={{ color: stat.color }} />
                <p className="text-3xl font-black leading-none" style={{ color: stat.color }}>{stat.value}</p>
                <p className="text-xs mt-1 font-medium" style={{ color: "#6b7280" }}>{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Suggerimento Telegram — UI differenziata in base allo stato:
              - bot già configurato (hasTelegram) → nascosto
              - add-on acquistato ma bot non configurato → CTA "Configura ora"
              - add-on NON acquistato → CTA "Acquista add-on" verso /abbonamento */}
          {!hasTelegram && (
            <div
              className="rounded-2xl p-4 mb-6 text-left flex gap-3 items-start"
              style={{ background: "#f0f9ff", border: "1.5px solid #bae6fd", animation: "wiz-slide-up 0.45s ease-out 0.35s both" }}
              data-testid="wizard-telegram-suggestion"
            >
              <Bot className="h-5 w-5 text-sky-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-sky-900 mb-0.5">
                  {telegramAddonOwned ? "Configura il tuo bot Telegram" : "Attiva l'assistente Telegram"}
                </p>
                <p className="text-xs text-sky-700 leading-relaxed mb-2">
                  {telegramAddonOwned
                    ? "Hai già acquistato l'add-on. Manca solo l'ultimo step: collegare il bot per ricevere notifiche e interrogare il gestionale in chat."
                    : "Ricevi notifiche su temperature, pulizie e segnalazioni direttamente in chat, senza aprire l'app."}
                </p>
                <button
                  onClick={() => navigate(telegramAddonOwned ? "/integrazioni/telegram" : "/abbonamento")}
                  className="text-xs font-bold text-sky-600 hover:text-sky-800 underline underline-offset-2 transition-colors"
                  data-testid="wizard-telegram-cta"
                >
                  {telegramAddonOwned ? "Configura ora →" : "Aggiungi al piano →"}
                </button>
              </div>
            </div>
          )}

          {/* CTA */}
          <button
            onClick={() => { if (onComplete) onComplete(); navigate("/"); toast.success("Configurazione completata! Benvenuto in Yollo."); }}
            className="w-full py-4 rounded-2xl text-base font-black text-white flex items-center justify-center gap-2 transition-all active:scale-[0.97] hover:opacity-90"
            style={{
              background: "linear-gradient(135deg, #6C63FF 0%, #4f46e5 100%)",
              boxShadow: "0 4px 20px rgba(108,99,255,0.35)",
              animation: "wiz-slide-up 0.45s ease-out 0.45s both",
            }}
            data-testid="wizard-complete-cta"
          >
            Inizia a far crescere la tua attività con Yollo
            <ChevronRight className="h-5 w-5" />
          </button>

          <p
            className="text-xs mt-4"
            style={{ color: "#9ca3af", animation: "wiz-slide-up 0.45s ease-out 0.55s both" }}
          >
            Puoi sempre modificare le impostazioni dal menu
          </p>
        </div>
      </div>
    );
  }

  // ─── Preview mode render: left guide panel (desktop) + bottom bar (mobile) ─
  if (isPreviewMode) {
    const isLast = currentStep.key === "segnalazioni";

    const previewContent = {
      calendario: (
        <div className="space-y-3">
          <p className="text-sm text-slate-300 leading-relaxed">
            Da qui vedi tutto quello che succede ogni giorno nel tuo locale: pulizie, temperature, task. Usalo ogni mattina per verificare che le attività siano state completate.
          </p>
          <div className="rounded-xl border border-slate-700 bg-slate-900 p-3 space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-200">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 shrink-0" />
              <span>Verde = completato</span>
            </div>
            <div className="flex items-center gap-2 text-xs font-medium text-slate-200">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400 shrink-0" />
              <span>Rosso = in ritardo / non eseguito</span>
            </div>
            <p className="text-xs text-slate-400 pt-1 border-t border-slate-800">
              Ogni mattina, un'occhiata al calendario ti basta per sapere come sta andando il tuo locale.
            </p>
          </div>
        </div>
      ),
      segnalazioni: (
        <div className="space-y-3">
          <p className="text-sm text-slate-300 leading-relaxed">
            In questa area puoi vedere le varie richieste e segnalazioni dei tuoi operatori. Rispondi direttamente dall'app o apri tu stesso una segnalazione verso lo staff.
          </p>
          <div className="space-y-1.5">
            {[
              { color: "#f87171", label: "Problematiche", desc: "Guasti, carenze, situazioni da risolvere" },
              { color: "#fbbf24", label: "Proposte",       desc: "Idee per migliorare il locale" },
              { color: "#60a5fa", label: "Informative",    desc: "Comunicazioni generali allo staff" },
            ].map(item => (
              <div key={item.label} className="flex items-start gap-2.5 p-2.5 rounded-lg border border-slate-700 bg-slate-900">
                <div className="w-2.5 h-2.5 rounded-full shrink-0 mt-0.5" style={{ background: item.color }} />
                <div>
                  <p className="text-xs font-semibold text-slate-100">{item.label}</p>
                  <p className="text-xs text-slate-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    };

    // Progress pill component (shared between desktop + mobile)
    const ProgressPills = () => (
      <div className="flex items-center gap-1">
        {WIZARD_STEPS.map((s, i) => (
          <div key={s.key} className={`rounded-full transition-all ${
            i < currentIndex
              ? "w-2 h-2 bg-emerald-400"
              : i === currentIndex
                ? "w-6 h-2 bg-indigo-500 shadow-[0_0_8px_rgba(108,99,255,0.9)]"
                : "w-2 h-2 bg-slate-700"
          }`} />
        ))}
        <span className="text-[10px] text-slate-400 ml-1 tabular-nums whitespace-nowrap">
          {currentIndex + 1}/{WIZARD_STEPS.length}
        </span>
      </div>
    );

    return (
      <>
        {/* ── Overlay fade durante il cambio di pagina (evita flash di skeleton/loading) ── */}
        <div
          className="fixed inset-0 z-[125] pointer-events-none transition-opacity duration-500 ease-out"
          style={{
            background: "linear-gradient(135deg, rgba(2,6,23,0.85) 0%, rgba(15,23,42,0.78) 100%)",
            backdropFilter: "blur(8px)",
            opacity: showCurtain ? 1 : 0,
          }}
          aria-hidden="true"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 rounded-full border-2 border-indigo-300/30 border-t-indigo-300 animate-spin" />
              <p className="text-xs uppercase tracking-widest font-bold text-indigo-300">Caricamento anteprima</p>
            </div>
          </div>
        </div>

        {/* ── Floating badge over main content ── */}
        <div
          className="fixed top-5 right-5 z-[115] pointer-events-none px-3 py-1.5 rounded-full border text-xs font-bold uppercase tracking-widest hidden md:flex items-center gap-1.5"
          style={{
            background: "rgba(99,102,241,0.08)",
            borderColor: "rgba(99,102,241,0.4)",
            color: "#818cf8",
            backdropFilter: "blur(8px)",
            boxShadow: "0 0 16px rgba(99,102,241,0.15)",
            animation: "pulse 2s cubic-bezier(0.4,0,0.6,1) infinite"
          }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 inline-block" style={{ animation: "pulse 1.5s ease-in-out infinite" }} />
          Modalità Configurazione
        </div>

        {/* ── Desktop: dark guide panel replacing the sidebar ── */}
        <div
          className="hidden md:flex fixed left-0 top-0 w-64 h-full z-[110] flex-col"
          style={{
            background: "#020617",
            borderRight: "1px solid rgba(108,99,255,0.3)",
            boxShadow: "4px 0 24px rgba(108,99,255,0.15)"
          }}
          data-testid="wizard-guide-panel"
        >
          {/* Header */}
          <div className="px-5 py-4 shrink-0" style={{ borderBottom: "1px solid rgba(108,99,255,0.2)" }}>
            <ProgressPills />
            <div className="mt-2 mb-1">
              <WizardGlobalBadge
                index={wizardMeta.wizard_index}
                total={wizardMeta.total_wizards}
                completed={wizardMeta.completed_wizards}
                dark={true}
              />
            </div>
            <p className="text-xs font-bold text-indigo-400 uppercase tracking-wider mt-2">{title}</p>
            <p className="text-xs text-slate-400 mt-0.5 leading-snug">{subtitle}</p>
          </div>

          {/* Content — solo questo scrolla */}
          <div className="flex-1 px-4 py-4 overflow-y-auto min-h-0">
            {previewContent[currentStep.key]}
          </div>

          {/* Actions — sempre visibili in fondo */}
          <div className="px-4 pb-6 pt-3 shrink-0 space-y-2" style={{ borderTop: "1px solid rgba(108,99,255,0.15)" }}>
            <button
              onClick={isLast ? completeSegnalazioni : completeCalendario}
              className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #6C63FF 0%, #4f46e5 100%)", boxShadow: "0 4px 16px rgba(108,99,255,0.4)" }}
              data-testid="wizard-guide-panel-continua"
            >
              {isLast ? "Completa configurazione" : "Continua"}
              <ChevronRight className="h-4 w-4" />
            </button>
            {currentIndex > 0 && (
              <button
                onClick={goPrev}
                className="w-full text-xs text-slate-400 hover:text-white flex items-center justify-center gap-1 py-1.5 transition-colors"
                data-testid="wizard-guide-panel-indietro"
              >
                <ChevronLeft className="h-3 w-3" /> Indietro
              </button>
            )}
            <button
              onClick={() => logout()}
              className="w-full text-xs text-slate-500 hover:text-slate-300 flex items-center justify-center gap-1.5 py-1.5 transition-colors"
              data-testid="wizard-guide-panel-logout"
            >
              <LogOut className="h-3 w-3" /> Esci e riprendi dopo
            </button>
          </div>
        </div>

        {/* ── Mobile: barra compatta in fondo — mostra solo titolo + azioni ── */}
        {/* La pagina è visibile sopra; non copriamo lo schermo con la descrizione */}
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 z-[110] rounded-t-2xl"
          style={{
            background: "#020617",
            borderTop: "2px solid rgba(108,99,255,0.5)",
            boxShadow: "0 -8px 32px rgba(108,99,255,0.25)",
          }}
          data-testid="wizard-guide-bar"
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-2.5 pb-1">
            <div className="w-8 h-1 rounded-full bg-slate-700" />
          </div>

          {/* Titolo compatto */}
          <div className="px-5 pt-1 pb-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-bold text-indigo-400 uppercase tracking-wider">{title}</p>
              <p className="text-xs text-slate-400 mt-0.5 leading-snug line-clamp-2">{subtitle}</p>
            </div>
            <button
              onClick={() => logout()}
              data-testid="wizard-guide-bar-logout"
              className="flex-shrink-0 flex flex-col items-center gap-0.5 text-slate-500 hover:text-slate-300 transition-colors pt-0.5"
            >
              <LogOut className="h-4 w-4" />
              <span className="text-[9px] leading-tight text-center">Esci</span>
            </button>
          </div>

          {/* Azioni */}
          <div className="px-5 pb-6 pt-2 flex gap-2" style={{ borderTop: "1px solid rgba(108,99,255,0.15)" }}>
            {currentIndex > 0 && (
              <button
                onClick={goPrev}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200 transition-colors whitespace-nowrap px-4 py-2.5 rounded-xl border border-slate-700"
                data-testid="wizard-guide-bar-indietro"
              >
                <ChevronLeft className="h-3 w-3" /> Indietro
              </button>
            )}
            <button
              onClick={isLast ? completeSegnalazioni : completeCalendario}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-[0.98]"
              style={{ background: "linear-gradient(135deg, #6C63FF 0%, #4f46e5 100%)", boxShadow: "0 4px 16px rgba(108,99,255,0.4)" }}
              data-testid="wizard-guide-bar-continua"
            >
              {isLast ? "Completa" : "Continua"} <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
    >
      <style>{`
        [data-testid="onboarding-wizard"] input:not([type="checkbox"]):not([type="color"]),
        [data-testid="onboarding-wizard"] textarea {
          border-color: #cbd5e1 !important;
          color: #1e293b !important;
        }
        [data-testid="onboarding-wizard"] input::placeholder,
        [data-testid="onboarding-wizard"] textarea::placeholder {
          color: #94a3b8 !important;
          opacity: 1 !important;
        }
        [data-testid="onboarding-wizard"] [data-radix-select-trigger] {
          border-color: #cbd5e1 !important;
          color: #1e293b !important;
        }
      `}</style>
      <div
        data-testid="onboarding-wizard"
        className="w-full max-w-xl bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <div className="flex-1 min-w-0">
            {/* Multi-wizard progress (solo se > 1 wizard) */}
            <WizardGlobalBadge
              index={wizardMeta.wizard_index}
              total={wizardMeta.total_wizards}
              completed={wizardMeta.completed_wizards}
              dark={false}
            />
            <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-0.5 mt-1">
              Configurazione iniziale
            </p>
            <h2 className="text-lg font-bold leading-tight">{title}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
          </div>
          <button
            onClick={() => logout()}
            data-testid="wizard-logout-btn"
            title="Esci e riprendi un secondo momento"
            className="flex-shrink-0 ml-4 flex flex-col items-center gap-1 text-slate-400 hover:text-slate-700 transition-colors group"
          >
            <LogOut className="h-4 w-4 group-hover:text-slate-700" />
            <span className="text-[10px] leading-tight text-center font-medium">Riprendi<br/>dopo</span>
          </button>
        </div>

        {/* Progress */}
        <WizardProgress currentIndex={currentIndex} progress={progress} steps={WIZARD_STEPS} />

        {/* Step nav breadcrumb */}
        <div className="px-6 py-2 flex items-center gap-1.5 border-b border-border bg-muted/20">
          {currentIndex > 0 && (
            <button
              onClick={goPrev}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 transition-colors"
            >
              <ChevronLeft className="h-3 w-3" /> Indietro
            </button>
          )}
          <span className="text-xs text-slate-500 ml-auto">
            Step {currentIndex + 1} di {WIZARD_STEPS.length}
          </span>
        </div>

        {/* Content */}
        <div ref={wizardContentRef} className="flex-1 overflow-y-auto px-6 py-4">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
